'use client';

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { LocalizedLink } from '../LocalizedLink';
import type { NavigationNode } from '@/hooks/useNavigationMenu';

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

function MobileSubList({
  nodes,
  onItemClick,
  depth = 0,
}: {
  nodes: NavigationNode[];
  onItemClick: () => void;
  depth?: number;
}) {
  if (!nodes.length) return null;

  return (
    <ul
      className={cx(
        'space-y-1 border-l border-white/30',
        depth > 0 ? 'ml-3 pl-3' : 'pl-4'
      )}
    >
      {nodes.map((child) => (
        <li key={child.id}>
          <LocalizedLink
            href={child.path}
            className="block py-2 text-[15px] text-white/90 hover:text-white"
            onClick={onItemClick}
          >
            {child.title}
          </LocalizedLink>

          {child.children.length > 0 && (
            <MobileSubList
              nodes={child.children}
              onItemClick={onItemClick}
              depth={depth + 1}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

export function MobileMenu({
  menus,
  onNavigate,
}: {
  menus: NavigationNode[];
  onNavigate: () => void;
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <ul className="flex flex-col divide-y divide-white/20">
      {menus.map((menu) => {
        const isLeaf = menu.children.length === 0;
        const isExpanded = expandedId === menu.id;

        if (isLeaf) {
          return (
            <li key={menu.id}>
              <LocalizedLink
                href={menu.path}
                className="block py-3 font-medium text-white"
                onClick={onNavigate}
              >
                {menu.title}
              </LocalizedLink>
            </li>
          );
        }

        return (
          <li key={menu.id}>
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={`mobile-menu-panel-${menu.id}`}
              onClick={() => setExpandedId(isExpanded ? null : menu.id)}
              className="flex w-full items-center justify-between gap-2 py-3 text-left font-medium text-white"
            >
              <span>{menu.title}</span>
              <ChevronDown
                className={cx(
                  'h-4 w-4 shrink-0 transition-transform duration-200',
                  isExpanded && 'rotate-180'
                )}
              />
            </button>

            {isExpanded && (
              <div id={`mobile-menu-panel-${menu.id}`} className="pb-3">
                <MobileSubList nodes={menu.children} onItemClick={onNavigate} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}